import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Gift, Ticket, Calendar, MapPin, Trophy, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const TOMBOLA_IMAGE = "https://customer-assets.emergentagent.com/job_cultural-events-5/artifacts/jexv1poc_BRIOUS%E2%80%99O%20FOLIES%20%2816%29.pdf";

export default function Tombola() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-b from-fuchsia-50 to-white" data-testid="tombola-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/evenements">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('eventsExt.seeAllEvents')}
            </Button>
          </Link>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-amber-100 text-amber-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
            <Gift className="inline h-4 w-4 mr-2" />
            {t('eventsExt.featured')}
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-zinc-900 mb-4">
            {t('tombola.title')}
          </h1>
          <h2 className="text-2xl md:text-3xl text-fuchsia-600 font-semibold mb-6">
            {t('tombola.winBalloon')}
          </h2>
          <p className="text-zinc-600 max-w-2xl mx-auto text-lg">
            {t('tombola.description')}
          </p>
        </div>

        {/* Event Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="border-0 shadow-lg text-center">
            <CardContent className="p-6">
              <Ticket className="h-10 w-10 text-fuchsia-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">{t('tombola.ticketPrice')}</h3>
              <p className="text-3xl font-bold text-fuchsia-600">5€</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg text-center">
            <CardContent className="p-6">
              <Calendar className="h-10 w-10 text-lime-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">{t('tombola.drawTime')}</h3>
              <p className="text-zinc-600">{t('tombola.drawDate')}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg text-center">
            <CardContent className="p-6">
              <MapPin className="h-10 w-10 text-amber-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">{t('venir.subtitle')}</h3>
              <p className="text-zinc-600">Domaine de la Briouse</p>
            </CardContent>
          </Card>
        </div>

        {/* Lots */}
        <Card className="border-0 shadow-xl mb-12 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Trophy className="h-6 w-6" />
              Les Lots à Gagner
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-b from-amber-50 to-white rounded-xl border-2 border-amber-200">
                <div className="text-4xl mb-4">🥇</div>
                <h4 className="font-bold text-xl text-amber-600 mb-2">1er Prix</h4>
                <p className="text-zinc-700 font-medium">Vol en Montgolfière</p>
                <p className="text-zinc-500 text-sm">pour 2 personnes</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-b from-zinc-100 to-white rounded-xl border-2 border-zinc-200">
                <div className="text-4xl mb-4">🥈</div>
                <h4 className="font-bold text-xl text-zinc-600 mb-2">2ème Prix</h4>
                <p className="text-zinc-700 font-medium">Panier Gourmand</p>
                <p className="text-zinc-500 text-sm">Produits du terroir</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-b from-amber-100/50 to-white rounded-xl border-2 border-amber-100">
                <div className="text-4xl mb-4">🥉</div>
                <h4 className="font-bold text-xl text-amber-700 mb-2">3ème Prix</h4>
                <p className="text-zinc-700 font-medium">Bon Cadeau Équitation</p>
                <p className="text-zinc-500 text-sm">Stage découverte</p>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-zinc-600 flex items-center justify-center gap-2">
                <Star className="h-5 w-5 text-amber-500" />
                Et de nombreux autres lots à gagner !
              </p>
            </div>
          </CardContent>
        </Card>

        {/* HelloAsso Widget Integration */}
        <Card className="border-0 shadow-xl mb-12">
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              <Ticket className="inline h-6 w-6 mr-2 text-fuchsia-600" />
              {t('tombola.participate')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* HelloAsso Full Widget */}
            <iframe 
              id="haWidget" 
              title="Tombola HelloAsso"
              allowTransparency="true" 
              scrolling="auto" 
              src="https://www.helloasso.com/associations/brious-o-folies/evenements/voyage-en-mongolfiere/widget" 
              style={{ width: '100%', height: '750px', border: 'none' }}
            />
          </CardContent>
        </Card>

        {/* Alternative: Bouton simple */}
        <div className="text-center mb-12">
          <p className="text-zinc-600 mb-4">Ou accédez directement à la billetterie :</p>
          <iframe 
            id="haWidgetButton" 
            title="Bouton HelloAsso"
            allowTransparency="true" 
            src="https://www.helloasso.com/associations/brious-o-folies/evenements/voyage-en-mongolfiere/widget-bouton" 
            style={{ width: '100%', height: '70px', border: 'none' }}
          />
        </div>

        {/* Règlement */}
        <Card className="border-0 shadow-lg bg-zinc-50">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-4">{t('tombola.rules')}</h3>
            <ul className="text-sm text-zinc-600 space-y-2">
              <li>• Tirage au sort effectué en présence d'un huissier</li>
              <li>• Les lots non réclamés sous 3 mois seront remis en jeu</li>
              <li>• Un seul lot par participant (le plus important)</li>
              <li>• Les membres du bureau de l'association ne peuvent pas participer</li>
              <li>• Règlement complet disponible sur demande</li>
            </ul>
            <p className="text-xs text-zinc-500 mt-4">
              Voir les <Link to="/mentions-legales" className="text-fuchsia-600 hover:underline">conditions générales</Link> pour plus d'informations.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
